import { z } from "zod";
import { UuidSchema, PlayerNameSchema, MoneySchema, EpochMillisSchema } from "./common.js";
/**
 * Actions admin (WEBAPI_SPEC §6, A1-A34) — request/response DTOs.
 * Le BFF ajoute l'identité de l'admin agissant (staff/staffName/staffUuid) avant de relayer à WebAPI.
 * Ces schémas décrivent les corps reçus par le BFF depuis le navigateur ; le BFF valide, gate le rôle,
 * puis forward. Noms alignés 1:1 sur le spec.
 */
/** Résultat générique d'action (miroir de {ok:true,...} / enveloppe d'erreur). */
export const ActionResultSchema = z.object({
    ok: z.boolean(),
    error: z.string().optional(),
    message: z.string().optional(),
    before: z.unknown().optional(),
    after: z.unknown().optional(),
});
// ── 6.1 Modération (A1-A10) ─────────────────────────────────────────────────
/** A1 — ban. */
export const BanReqSchema = z.object({
    uuid: UuidSchema,
    name: PlayerNameSchema.optional(),
    reason: z.string().max(512),
    ip: z.string().optional(),
});
/** A2 — tempban. */
export const TempBanReqSchema = BanReqSchema.extend({
    durationMs: z.number().int().positive(),
});
/** A3 — unban. */
export const UnbanReqSchema = z.object({
    uuid: UuidSchema,
    reason: z.string().max(512),
});
/** A4 — kick (online). */
export const KickReqSchema = z.object({
    uuid: UuidSchema,
    reason: z.string().max(512),
});
/** A5 — mute. */
export const MuteReqSchema = z.object({
    uuid: UuidSchema,
    name: PlayerNameSchema.optional(),
    reason: z.string().max(512),
});
/** A6 — tempmute. */
export const TempMuteReqSchema = MuteReqSchema.extend({
    durationMs: z.number().int().positive(),
});
/** A7 — unmute. */
export const UnmuteReqSchema = z.object({
    uuid: UuidSchema,
    reason: z.string().max(512),
});
/** A8 — warn. */
export const WarnReqSchema = z.object({
    uuid: UuidSchema,
    name: PlayerNameSchema.optional(),
    reason: z.string().max(512),
});
/** A9 — freeze. */
export const FreezeReqSchema = z.object({
    uuid: UuidSchema,
    frozen: z.boolean(),
});
/** A10 — vanish. */
export const VanishReqSchema = z.object({
    uuid: UuidSchema,
    vanished: z.boolean(),
});
/** Discriminateur d'opération de modération pour /api/admin/moderation/:op. */
export const MODERATION_OPS = [
    "ban",
    "tempban",
    "unban",
    "kick",
    "mute",
    "tempmute",
    "unmute",
    "warn",
    "freeze",
    "vanish",
];
export const ModerationOpSchema = z.enum(MODERATION_OPS);
// ── 6.2 Rangs & permissions (A11-A12) ───────────────────────────────────────
/** A11 — set rank. */
export const SetRankReqSchema = z.object({
    uuid: UuidSchema,
    rankId: z.string(),
});
/** A11b — set free adventure rank. */
export const SetAdventureRankReqSchema = z.object({
    uuid: UuidSchema,
    level: z.number().int().min(1).max(50),
});
/** A12 — grant/revoke perm (transient, online). */
export const SetPermReqSchema = z.object({
    uuid: UuidSchema,
    perm: z.string(),
    grant: z.boolean(),
});
// ── 6.3 Progression (A15-A18) ───────────────────────────────────────────────
/** A15 — level set (xp absolu). */
export const SetLevelReqSchema = z.object({
    uuid: UuidSchema,
    xp: z.number().int().nonnegative(),
});
/** A15b — level set by target level. */
export const SetLevelNumberReqSchema = z.object({
    uuid: UuidSchema,
    level: z.number().int().min(1),
});
/** A16 — level add (xp delta). */
export const AddLevelReqSchema = z.object({
    uuid: UuidSchema,
    xp: z.number().int(),
});
/** A17 — job set (online only). */
export const SetJobLevelReqSchema = z.object({
    uuid: UuidSchema,
    job: z.string(),
    xp: z.number().nonnegative(),
});
/** A18 — talents reset. */
export const TalentsResetReqSchema = z.object({
    uuid: UuidSchema,
});
// ── 6.4 Économie & tokens (A19-A24) ─────────────────────────────────────────
/** A19-A21 — economy set/add/withdraw. */
export const EconomyReqSchema = z.object({
    uuid: UuidSchema,
    amount: MoneySchema,
    reason: z.string().max(255).optional(),
});
export const ECONOMY_OPS = ["set", "add", "withdraw"];
export const EconomyOpSchema = z.enum(ECONOMY_OPS);
/** A22-A24 — tokens set/add/remove. */
export const TokensReqSchema = z.object({
    uuid: UuidSchema,
    amount: z.number().int(),
});
export const TOKENS_OPS = ["set", "add", "remove"];
export const TokensOpSchema = z.enum(TOKENS_OPS);
// ── 6.5 Items / inventaire (A25-A32) ────────────────────────────────────────
/** A25 — give vanilla. */
export const GiveVanillaReqSchema = z.object({
    uuid: UuidSchema,
    material: z.string(),
    amount: z.number().int().positive().max(6400),
    name: z.string().optional(),
    lore: z.array(z.string()).optional(),
    enchants: z.array(z.object({ key: z.string(), level: z.number().int() })).optional(),
});
/** A26 — give custom. */
export const GiveCustomReqSchema = z.object({
    uuid: UuidSchema,
    id: z.string(),
    amount: z.number().int().positive().max(6400),
});
/** A26b — regive craft generated by Etheria_Crafting. */
export const GiveCraftReqSchema = z.object({
    uuid: UuidSchema,
    id: z.string(),
    amount: z.number().int().positive().max(6400),
});
/** A27 — give loot. */
export const GiveLootReqSchema = z.object({
    uuid: UuidSchema,
    ilvl: z.number().int().positive(),
    minRarity: z.string().optional(),
    classId: z.string().optional(),
});
/** A28 — give enchant book. */
export const GiveEnchantBookReqSchema = z.object({
    uuid: UuidSchema,
    defId: z.string(),
    tier: z.number().int().positive(),
});
/** A29 — give kit. */
export const GiveKitReqSchema = z.object({
    uuid: UuidSchema,
    kitId: z.string(),
});
export const GIVE_KINDS = ["vanilla", "custom", "craft", "loot", "enchantbook", "kit"];
export const GiveKindSchema = z.enum(GIVE_KINDS);
export const INV_CONTAINERS = ["main", "armor", "offhand", "ender"];
export const InvContainerSchema = z.enum(INV_CONTAINERS);
export const InvSlotRefSchema = z.object({
    container: InvContainerSchema.default("main"),
    slot: z.number().int(),
});
/** Item minimal pour setslot (matière + méta optionnelle). */
export const SlotItemSchema = z.object({
    material: z.string(),
    amount: z.number().int().positive().default(1),
    name: z.string().optional(),
    lore: z.array(z.string()).optional(),
    customId: z.string().optional(),
});
/** A30 — inv setslot (item ou null). */
export const InvSetSlotReqSchema = z.object({
    uuid: UuidSchema,
    container: InvContainerSchema.default("main"),
    slot: z.number().int(),
    item: SlotItemSchema.nullable(),
});
/** A31 — inv clearslot. */
export const InvClearSlotReqSchema = z.object({
    uuid: UuidSchema,
    container: InvContainerSchema.default("main"),
    slot: z.number().int(),
});
/** A32 — inv remove (match). */
export const InvRemoveReqSchema = z.object({
    uuid: UuidSchema,
    match: z.object({
        material: z.string().optional(),
        customId: z.string().optional(),
        rarity: z.string().optional(),
    }),
    amount: z.number().int().positive().optional(),
});
export const InvMoveReqSchema = z.object({
    uuid: UuidSchema,
    from: InvSlotRefSchema,
    to: InvSlotRefSchema,
});
export const InvCopyReqSchema = z.object({
    uuid: UuidSchema,
    from: InvSlotRefSchema,
    to: InvSlotRefSchema.optional(),
    amount: z.number().int().positive().max(64).optional(),
});
export const INV_OPS = ["setslot", "clearslot", "remove", "move", "copy"];
export const InvOpSchema = z.enum(INV_OPS);
// ── 6.6 Joueur & serveur (A33-A34) ──────────────────────────────────────────
/** A33 — player: teleport. */
export const TeleportReqSchema = z.object({
    uuid: UuidSchema,
    world: z.string(),
    x: z.number(),
    y: z.number(),
    z: z.number(),
    yaw: z.number().optional(),
    pitch: z.number().optional(),
});
/** A33 — player: gamemode. */
export const GameModeReqSchema = z.object({
    uuid: UuidSchema,
    mode: z.enum(["SURVIVAL", "CREATIVE", "ADVENTURE", "SPECTATOR"]),
});
/** A33 — player: heal / feed (juste uuid). */
export const PlayerUuidReqSchema = z.object({ uuid: UuidSchema });
/** A33 — player: fly. */
export const FlyReqSchema = z.object({
    uuid: UuidSchema,
    on: z.boolean(),
});
export const PLAYER_OPS = ["teleport", "gamemode", "heal", "feed", "fly"];
export const PlayerOpSchema = z.enum(PLAYER_OPS);
/** A34 — server: broadcast. */
export const BroadcastReqSchema = z.object({
    message: z.string().min(1).max(1024),
});
/** A34 — server: pm. */
export const PmReqSchema = z.object({
    uuid: UuidSchema,
    message: z.string().min(1).max(1024),
});
export const SERVER_OPS = ["broadcast", "pm", "save-all", "reload"];
export const ServerOpSchema = z.enum(SERVER_OPS);
// ── Audit (R-side admin logs, etheria_web_audit) ────────────────────────────
/** Une ligne d'audit web (§9). */
export const AuditEntrySchema = z.object({
    id: z.number().int(),
    ts: EpochMillisSchema,
    actorUuid: UuidSchema.nullable(),
    actorName: z.string().nullable(),
    actorIp: z.string().nullable(),
    action: z.string(),
    targetUuid: UuidSchema.nullable(),
    targetName: z.string().nullable(),
    params: z.unknown().nullable(),
    result: z.enum(["ok", "error", "forbidden"]),
    message: z.string().nullable(),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
});
export const AuditQuerySchema = z.object({
    actor: UuidSchema.optional(),
    target: UuidSchema.optional(),
    action: z.string().optional(),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().min(0).default(0),
});
/** Config live (proxy WebAdmin) — GET/PUT config/{plugin}/{key}. */
export const ConfigGetReqSchema = z.object({
    plugin: z.string(),
    key: z.string(),
});
export const ConfigPutReqSchema = z.object({
    plugin: z.string(),
    key: z.string(),
    value: z.unknown(),
});
// ── Config plugins — inventaire structuré (WebAdmin AdminRegistry) ───────────
/**
 * Type d'une valeur de configuration (pilote le widget d'édition côté admin :
 * bascule pour bool, champ nombre, textarea pour string/list, éditeur JSON pour object).
 */
export const CONFIG_ENTRY_TYPES = [
    "string",
    "number",
    "boolean",
    "list",
    "object",
];
export const PluginConfigEntryTypeSchema = z.enum(CONFIG_ENTRY_TYPES);
/**
 * Une clé de configuration d'un plugin (miroir d'une entrée AdminRegistry WebAdmin).
 *  - plugin   : nom du plugin propriétaire (ex « Etheria_Mobs »).
 *  - key      : chemin pointé de la clé (ex « spawn.night-cap »).
 *  - value    : valeur courante (forme libre ; interprétée selon `type`).
 *  - type     : type déclaré de la valeur (pilote l'éditeur).
 *  - readOnly : true si la clé est protégée en écriture côté WebAdmin (optionnel).
 *  - label    : libellé humain FR (optionnel).
 */
export const PluginConfigEntrySchema = z.object({
    plugin: z.string(),
    key: z.string(),
    value: z.unknown(),
    type: PluginConfigEntryTypeSchema,
    readOnly: z.boolean().optional(),
    label: z.string().optional(),
});
/** Résumé d'un plugin configurable (pour la liste de gauche du panneau Config). */
export const PluginSummarySchema = z.object({
    /** Nom d'affichage / clé du module (ex « Etheria_Mobs » ou son alias minuscule). */
    plugin: z.string(),
    /** Nom exact du plugin Bukkit (ex « Etheria_Mobs »). */
    name: z.string().optional(),
    /** Fichiers YAML éditables exposés pour ce plugin (ex ["config.yml","mobs.yml"]). */
    files: z.array(z.string()).default([]),
    /** true si le plugin a enregistré un AdminModule (édition + reload chaud) ; false = repli fichiers. */
    registered: z.boolean().optional(),
    /** Nombre de clés éditables exposées (optionnel). */
    keyCount: z.number().int().nonnegative().optional(),
    /** true si le plugin est actuellement chargé/activé (optionnel). */
    enabled: z.boolean().optional(),
});
/** Liste des plugins configurables exposés par WebAdmin. */
export const PluginListSchema = z.object({
    plugins: z.array(PluginSummarySchema),
});
/** Inventaire des clés d'un plugin donné (panneau de droite du panneau Config). */
export const PluginConfigEntryListSchema = z.object({
    plugin: z.string(),
    entries: z.array(PluginConfigEntrySchema),
});
// ── Config par FICHIER YAML (modèle réel WebAdmin PluginsController) ──────────
/**
 * Contenu d'un fichier de configuration d'un plugin (miroir de WebAdmin GET config/{plugin}/{file}).
 *  - plugin : nom du module/plugin propriétaire.
 *  - file   : nom du fichier YAML (ex « config.yml »).
 *  - raw    : contenu YAML brut (source de vérité éditable).
 *  - tree   : arbre clé→valeur parsé (aperçu structuré ; peut être vide si le parse échoue).
 *  - readOnly : true si WebAdmin est en lecture seule (les PUT sont refusés).
 */
export const PluginConfigFileSchema = z.object({
    plugin: z.string(),
    file: z.string(),
    raw: z.string(),
    tree: z.record(z.string(), z.unknown()).default({}),
    readOnly: z.boolean().optional(),
});
/** Corps d'écriture d'un fichier de config (YAML brut). Validé côté WebAdmin avant écriture. */
export const PluginConfigFilePutReqSchema = z.object({
    raw: z.string(),
});
/** Résultat d'écriture d'un fichier de config (sauvegarde + reload chaud). */
export const PluginConfigSaveResultSchema = z.object({
    ok: z.boolean(),
    saved: z.boolean().optional(),
    reloaded: z.boolean().optional(),
    appliedAt: EpochMillisSchema.optional(),
    error: z.string().optional(),
    message: z.string().optional(),
});
//# sourceMappingURL=admin.js.map